import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BasemapType } from '../../config/basemaps';

export interface MapView {
  center: [number, number]; // [longitude, latitude]
  zoom: number;
  rotation: number;
}

export interface MapExtent {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface MapState {
  view: MapView;
  extent: MapExtent | null;
  projection: string;
  currentBasemap: BasemapType;
  basemapError: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: MapState = {
  view: {
    center: [0, 0],
    zoom: 2,
    rotation: 0,
  },
  extent: null,
  projection: 'EPSG:3857', // Web Mercator (default for OpenLayers)
  currentBasemap: 'osm', // Default to OpenStreetMap
  basemapError: null,
  isLoading: false,
  error: null,
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setMapView: (state, action: PayloadAction<Partial<MapView>>) => {
      state.view = { ...state.view, ...action.payload };
    },
    setMapCenter: (state, action: PayloadAction<[number, number]>) => {
      state.view.center = action.payload;
    },
    setMapZoom: (state, action: PayloadAction<number>) => {
      state.view.zoom = action.payload;
    },
    setMapRotation: (state, action: PayloadAction<number>) => {
      state.view.rotation = action.payload;
    },
    setMapExtent: (state, action: PayloadAction<MapExtent | null>) => {
      state.extent = action.payload;
    },
    setMapProjection: (state, action: PayloadAction<string>) => {
      state.projection = action.payload;
    },
    setCurrentBasemap: (state, action: PayloadAction<BasemapType>) => {
      state.currentBasemap = action.payload;
      state.basemapError = null; // Clear any previous errors when switching
    },
    setBasemapError: (state, action: PayloadAction<string | null>) => {
      state.basemapError = action.payload;
    },
    setMapLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setMapError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetMapView: (state) => {
      state.view = initialState.view;
      state.extent = null;
    },
  },
});

export const {
  setMapView,
  setMapCenter,
  setMapZoom,
  setMapRotation,
  setMapExtent,
  setMapProjection,
  setCurrentBasemap,
  setBasemapError,
  setMapLoading,
  setMapError,
  resetMapView,
} = mapSlice.actions;

export default mapSlice.reducer;
